// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { debug } from '@imprivata-cloud/common';
import { useMemo } from 'react';

import { endWorkflowAndCloseWindow, hideWindow, useAgent } from '@/api/agent';
import { handleAuthnArtifact } from '@/api/api';

import { AuthnFlow, type OutcomeReporter } from '../authn-flow';
import { useQueryParams } from './hooks/useQueryParams';

const buildArtifactRedirectUrl = (
  {
    redirectUri,
    authorizationCode,
    errorCode,
    errorDescription,
    state,
  }: Awaited<ReturnType<typeof handleAuthnArtifact>>,
  loginHint?: string,
) => {
  let url = redirectUri;

  debug('[login-hint] buildArtifactRedirectUrl', {
    hasAuthorizationCode: !!authorizationCode,
    errorCode,
    loginHint,
  });

  if (authorizationCode) {
    url += `?code=${authorizationCode}`;
  } else if (errorCode) {
    url += `?error=${errorCode}`;
    if (errorDescription) {
      url += `&error_description=${errorDescription}`;
    }
    // On the failure exit, echo the attempted username back to the RP so it can
    // pre-fill its login form on retry.
    if (loginHint) {
      url += `&login_hint=${encodeURIComponent(loginHint)}`;
    }
  }

  if (state) {
    url += `&state=${state}`;
  }

  return url;
};

const authnArtifactReporter = (rpRequestId: string): OutcomeReporter => ({
  onSuccess: async ({ authorizationCode }) => {
    const response = await handleAuthnArtifact({
      rpRequestId,
      authorizationCode,
    });

    debug('Authentication artifact processed successfully', {
      redirectUri: response.redirectUri,
      hasState: !!response.state,
      hasAuthorizationCode: !!response.authorizationCode,
    });

    hideWindow();

    return {
      redirectUrl: buildArtifactRedirectUrl(response),
      spanAttrs: { redirectUri: response.redirectUri, state: response.state },
    };
  },
  onFailure: async ({ errorCode, errorDescription, loginHint }) => {
    const response = await handleAuthnArtifact({
      rpRequestId,
      errorCode,
      errorDescription,
    });

    debug('Authentication artifact processed with failure response', {
      redirectUri: response.redirectUri,
      hasState: !!response.state,
      hasErrorCode: !!response.errorCode,
      hasErrorDescription: !!response.errorDescription,
    });

    // EAM exit
    // TODO: Verify with EAM
    endWorkflowAndCloseWindow();

    return {
      redirectUrl: buildArtifactRedirectUrl(response, loginHint),
      spanAttrs: {
        redirectUri: response.redirectUri,
        state: response.state,
        errorCode: response.errorCode,
        errorDescription: response.errorDescription,
      },
    };
  },
});

export const IcpCoreFlow: React.FC = () => {
  // Isolated EAM Agent Web API code. This hook must contain all and every instance of those Web APIs
  const showUI = useAgent();
  const { rpRequestId, ...launchConfig } = useQueryParams();
  const outcomeReporter = useMemo(
    () => authnArtifactReporter(rpRequestId),
    [rpRequestId],
  );

  return (
    <AuthnFlow
      launchConfig={launchConfig}
      showUI={showUI}
      outcomeReporter={outcomeReporter}
    />
  );
};
